/**
 * 時辰邊界與前後對比（標準十二時辰 2 小時塊）
 *
 * 子時跨日：23:00–01:00；23:00–23:59 日柱／年柱用次日（見 calendar）。
 */
import { CalendarInput, hourZhiInfo, DIZHI } from './calendar';
import { computeBazi, BaziInput, BaziResult } from './bazi';

/** 十二時辰起點（牆鐘小時）：子=23，丑=1 … 亥=21 */
export const SHICHEN_START_HOURS = [23, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19, 21] as const;

export interface ShichenInfo {
  /** 0=子 … 11=亥 */
  index: number;
  zhi: string;
  /** 顯示用：子時 23:00–01:00 */
  rangeLabel: string;
  /** 兩小時塊起點牆鐘（0–23） */
  startHour: number;
  /** 結束牆鐘小時（顯示用；子為 1） */
  endHourDisplay: number;
}

export interface DatedShichen extends ShichenInfo {
  /** 餵給 computeBazi 的代表時刻（塊內中點） */
  sample: CalendarInput;
  /** 該塊牆鐘起點（含日期） */
  rangeStart: CalendarInput;
  /** 該塊牆鐘終點（不含；已跨日則為翌日） */
  rangeEnd: CalendarInput;
}

export interface ShichenCompare {
  current: DatedShichen;
  prev: DatedShichen;
  next: DatedShichen;
  charts: {
    original: BaziResult;
    prev: BaziResult;
    next: BaziResult;
  };
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function partsFromDate(d: Date): CalendarInput {
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
  };
}

function toDate(p: CalendarInput): Date {
  return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, 0, 0);
}

function addMinutes(p: CalendarInput, minutes: number): CalendarInput {
  const d = toDate(p);
  d.setMinutes(d.getMinutes() + minutes);
  return partsFromDate(d);
}

/** 時支字 → 0..11（子=0） */
export function zhiToShichenIndex(zhi: string): number {
  const i = DIZHI.indexOf(zhi);
  return i >= 0 ? i : 0;
}

export function rangeLabelForIndex(index: number): string {
  const zhi = DIZHI[index];
  const start = SHICHEN_START_HOURS[index];
  if (index === 0) return `${zhi}時 23:00–01:00`;
  const end = (start + 2) % 24;
  return `${zhi}時 ${pad2(start)}:00–${pad2(end)}:00`;
}

export function shichenInfoAt(hour: number, minute: number): ShichenInfo {
  const { zhi } = hourZhiInfo(hour, minute);
  const index = zhiToShichenIndex(zhi);
  const startHour = SHICHEN_START_HOURS[index];
  const endHourDisplay = index === 0 ? 1 : (startHour + 2) % 24;
  return {
    index,
    zhi,
    rangeLabel: rangeLabelForIndex(index),
    startHour,
    endHourDisplay,
  };
}

/**
 * 給定當前日時，回傳該時辰塊在時間軸上的起點 Date。
 * 子時：若 hour>=23 → 當日 23:00；若 hour<1 → 昨日 23:00。
 */
export function currentShichenStart(input: CalendarInput): Date {
  const info = shichenInfoAt(input.hour, input.minute);
  const d = toDate(input);
  if (info.index === 0) {
    if (input.hour >= 23) {
      d.setHours(23, 0, 0, 0);
      return d;
    }
    // 00:00–00:59：子時起點是前一日 23:00
    d.setDate(d.getDate() - 1);
    d.setHours(23, 0, 0, 0);
    return d;
  }
  d.setHours(info.startHour, 0, 0, 0);
  return d;
}

function datedFromStart(start: Date, index: number): DatedShichen {
  const zhi = DIZHI[index];
  const startHour = SHICHEN_START_HOURS[index];
  const endHourDisplay = index === 0 ? 1 : (startHour + 2) % 24;
  const rangeStart = partsFromDate(start);
  const rangeEnd = addMinutes(rangeStart, 120);
  const sample = addMinutes(rangeStart, 60); // 中點
  return {
    index,
    zhi,
    rangeLabel: rangeLabelForIndex(index),
    startHour,
    endHourDisplay,
    sample,
    rangeStart,
    rangeEnd,
  };
}

/** 原局／前一時辰／後一時辰（含代表時刻） */
export function shichenNeighbors(input: CalendarInput): {
  current: DatedShichen;
  prev: DatedShichen;
  next: DatedShichen;
} {
  const start = currentShichenStart(input);
  const curInfo = shichenInfoAt(input.hour, input.minute);
  const current = datedFromStart(start, curInfo.index);

  const prevStart = new Date(start);
  prevStart.setHours(prevStart.getHours() - 2);
  const prevIndex = (curInfo.index + 11) % 12;
  const prev = datedFromStart(prevStart, prevIndex);

  const nextStart = new Date(start);
  nextStart.setHours(nextStart.getHours() + 2);
  const nextIndex = (curInfo.index + 1) % 12;
  const next = datedFromStart(nextStart, nextIndex);

  return { current, prev, next };
}

/** 三盤對比：性別同原局輸入 */
export function compareShichen(input: BaziInput): ShichenCompare {
  const { current, prev, next } = shichenNeighbors(input);
  const gender = input.gender;
  return {
    current,
    prev,
    next,
    charts: {
      original: computeBazi({ ...input, gender }),
      prev: computeBazi({ ...prev.sample, gender }),
      next: computeBazi({ ...next.sample, gender }),
    },
  };
}

/** 格式化日時標籤 */
export function formatSampleLabel(p: CalendarInput): string {
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)} ${pad2(p.hour)}:${pad2(p.minute)}`;
}
